import type { Transaction } from "@rocicorp/zero";
import { userFacingError } from "../../authorization";
import type { Schema } from "../../generatedDrizzleSchema";
import { generateId } from "../../id";
import type { Permission } from "../permissions";
import {
  type BuiltinRoleId,
  type NullableGrantEntry,
  grantHasPermission,
} from "../roles";
import { assertCustomRoleScope, type GrantInput } from "./grantShape";

export function assertRoleDoesNotGrant(
  role: {
    builtinRoleId?: BuiltinRoleId | null;
    customRole?: {
      rolePermissions: ReadonlyArray<{ permission: string }>;
    } | null;
  },
  permission: Permission
): void {
  if (
    grantHasPermission(
      {
        builtinRoleId: role.builtinRoleId ?? null,
        customRole: role.customRole ?? null,
      },
      permission
    )
  ) {
    throw userFacingError([
      'Role grants "',
      { permission },
      '" — pick a role that does not',
    ]);
  }
}

export function assertValidSpaceDefaultJoinRole(role: {
  builtinRoleId?: BuiltinRoleId | null;
  customRole?: {
    scopeKind: string;
    rolePermissions: ReadonlyArray<{ permission: string }>;
  } | null;
}): void {
  if (role.customRole) assertCustomRoleScope(role.customRole, "space");
  assertRoleDoesNotGrant(role, "space:manage");
}

export function grantKey(g: Partial<NullableGrantEntry>): string {
  return `${g.userGroupId ?? ""}:${g.userId ?? ""}:${g.serviceAccountId ?? ""}:${g.builtinRoleId ?? ""}:${g.customRoleId ?? ""}`;
}

export function dedupGrants<
  T extends { [K in keyof GrantInput]?: GrantInput[K] },
>(grants: readonly T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const g of grants) {
    const key = grantKey(g);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(g);
  }
  return out;
}

export async function insertGrantsDeduped(
  tx: Pick<Transaction<Schema>, "mutate">,
  grants: readonly GrantInput[],
  scopeId: string,
  tenantId: string
): Promise<void> {
  const now = Date.now();
  for (const grant of dedupGrants(grants)) {
    await tx.mutate.roleGrants.insert({
      id: generateId(),
      userGroupId: grant.userGroupId,
      userId: grant.userId,
      serviceAccountId: grant.serviceAccountId,
      builtinRoleId: (grant.builtinRoleId as BuiltinRoleId) ?? undefined,
      customRoleId: grant.customRoleId,
      scopeId,
      createdAt: now,
      tenantId,
    });
  }
}

type ExistingGrantRow = {
  id: string;
  userGroupId: string | null;
  userId: string | null;
  // Optional so callers/tests that predate service-account principals
  // type-check unchanged; real role_grants rows always carry the column.
  serviceAccountId?: string | null;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

export type GrantDiff = {
  toInsert: GrantInput[];
  toDelete: ExistingGrantRow[];
  removedGroupIds: string[];
  removedUserIds: string[];
  removedServiceAccountIds: string[];
};

export function diffGrants(
  existing: readonly ExistingGrantRow[],
  desired: readonly GrantInput[]
): GrantDiff {
  const desiredDeduped = dedupGrants(desired);
  const desiredKeys = new Set(desiredDeduped.map(grantKey));
  const existingKeys = new Set(existing.map(grantKey));

  const toDelete: ExistingGrantRow[] = [];
  const removedGroupIds: string[] = [];
  const removedUserIds: string[] = [];
  const removedServiceAccountIds: string[] = [];
  for (const row of existing) {
    if (desiredKeys.has(grantKey(row))) continue;
    toDelete.push(row);
    if (row.userGroupId) removedGroupIds.push(row.userGroupId);
    if (row.userId) removedUserIds.push(row.userId);
    if (row.serviceAccountId)
      removedServiceAccountIds.push(row.serviceAccountId);
  }

  const toInsert = desiredDeduped.filter((g) => !existingKeys.has(grantKey(g)));
  return {
    toInsert,
    toDelete,
    removedGroupIds,
    removedUserIds,
    removedServiceAccountIds,
  };
}

export async function applyGrantDiff(
  tx: Pick<Transaction<Schema>, "mutate">,
  scopeId: string,
  diff: {
    toInsert: readonly GrantInput[];
    toDelete: readonly { id: string }[];
  },
  tenantId: string
): Promise<void> {
  for (const row of diff.toDelete) {
    await tx.mutate.roleGrants.delete({ id: row.id });
  }
  await insertGrantsDeduped(tx, diff.toInsert, scopeId, tenantId);
}
